<script setup lang="ts">
import { ref } from "vue";
import type { CheckboxProps } from "./checkbox.types";
import { useCheckbox } from "./useCheckbox";
import { VisuallyHiddenInput } from "components/VisuallyHidden";
import { checkboxIndicatorWrapperClasses } from "./checkbox.styles";

defineOptions({ name: "ECheckbox" });

const props = withDefaults(defineProps<CheckboxProps>(), {
  color: "neutral",
  variant: "subtle",
  size: "md",
  modelValue: false,
  disabled: false,
  required: false,
  icon: "check",
  encased: false,
});

const buttonRef = ref<HTMLButtonElement | null>(null);

const {
  state,
  rootClasses,
  boxClasses,
  ariaChecked,
  ariaDisabled,
  dataDisabled,
  dataState,
  formValue,
  indicatorIcon,
  indicatorSize,
  indicatorClasses,
  WrapperComponent,
  onRootClick,
  onButtonClick,
  onSlotClick,
} = useCheckbox({ props, buttonRef });
</script>

<template>
  <component
    :is="WrapperComponent"
    :part="props.encased ? 'encased' : null"
    :color="props.encased ? 'mono' : undefined"
    :variant="props.encased ? props.variant : undefined"
    :size="props.encased ? 'sm' : undefined"
    :class="props.encased ? 'p-0!' : undefined"
  >
    <div part="base" exportparts="indicator-icon" :class="rootClasses" @click="onRootClick">
      <button
        ref="buttonRef"
        type="button"
        role="checkbox"
        part="control"
        :class="boxClasses"
        :aria-checked="ariaChecked"
        :aria-disabled="ariaDisabled"
        :data-disabled="dataDisabled"
        :data-state="dataState"
        @click="onButtonClick"
      >
        <span part="indicator" :class="checkboxIndicatorWrapperClasses">
          <e-icon
            exportparts="base:indicator-icon"
            :class="indicatorClasses"
            :name="indicatorIcon"
            :size="indicatorSize"
          />
        </span>
      </button>

      <slot @click="onSlotClick" />

      <visually-hidden-input
        :name="state.name?.value"
        :value="formValue"
        :required="state.required?.value"
        :disabled="state.disabled?.value"
        type="checkbox"
      />
    </div>
  </component>
</template>
